import type React from "react";
import { NavLink } from "react-router";
import type { VariantProps } from "tailwind-variants";

import type { ButtonBaseProps, ButtonButtonProps, ButtonLinkProps, ButtonSubmitProps } from "@/design-system/Button";
import { buttonVariants } from "@/design-system/Button";
import { Tooltip } from "@/design-system/Tooltip";
import { twResolve, twVariants } from "@/helpers/tw-resolve";

type IconButtonBaseProps = Omit<ButtonBaseProps, "title" | "children" | "className"> & {
  icon: React.ReactElement;
  title: string;
  variant?: IconButtonVariants["variant"];
  tone?: IconButtonVariants["tone"];
  size?: IconButtonVariants["size"];
  withTooltip?: boolean;
  isCircular?: boolean;
  ref?: React.Ref<HTMLAnchorElement | HTMLButtonElement>;
};

type IconButtonLinkProps = Omit<ButtonLinkProps, "isExternal"> & { isExternal?: never };

export type IconButtonProps = IconButtonBaseProps & (IconButtonLinkProps | ButtonButtonProps | ButtonSubmitProps);

export function IconButton({
  title,
  icon,
  size,
  type,
  href,
  target,
  state,
  form,
  variant = "soft",
  tone,
  disabled,
  "data-testid": dataTestId,
  withTooltip = true,
  isCircular,
  onClick,
  onFocus,
  onBlur,
  ref,
  ...props
}: IconButtonProps): React.ReactNode {
  const propsCommon = {
    "data-testid": dataTestId,
    "aria-label": title,
    className: twResolve(
      iconButtonVariants({
        variant,
        tone,
        size,
        type,
        content: "icon",
      }),
      "flex items-center justify-center px-0",
      isCircular && "rounded-full",
    ),
  };

  if (type === "link") {
    return (
      <Tooltip tooltip={withTooltip ? title : undefined} offset="overlap">
        <NavLink
          aria-disabled={disabled}
          to={href}
          ref={ref as React.Ref<HTMLAnchorElement>}
          onClick={(event) => {
            if (disabled) {
              event.preventDefault();
            }

            onClick?.(event);
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          {...{ ...propsCommon, state, target }}
        >
          {icon}
        </NavLink>
      </Tooltip>
    );
  }

  return (
    <Tooltip tooltip={withTooltip ? title : undefined} offset="overlap">
      <button
        {...propsCommon}
        type={type || "button"}
        disabled={disabled}
        form={form}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
        ref={ref as React.Ref<HTMLButtonElement>}
        {...props}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

export const iconButtonVariants = twVariants({
  extend: buttonVariants,
  variants: {
    size: {
      xs: "size-icon-inline!",
      sm: "size-icon-display!",
      md: "size-control-md!",
    },
  },
});

type IconButtonVariants = VariantProps<typeof iconButtonVariants>;
